import { Tabs, Typography } from "@hellocarbo/lasagne";
import { Euro, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { IndustryComparison, Percentile, Percentiles } from "@/types";
import {
  calculateEconomicCarbonIntensity,
  calculateSocialCarbonIntensity,
} from "@/utils/misc";

import Card from "../ui/Card";
import CannotCalculateIntensityMessage from "./CannotCalculateIntensityMessage";
import IntensityContent from "./IntensityContent";

enum COMPARISON_VIEWS {
  byEconomicIntensity = "economic",
  bySocialIntensity = "social",
}

type SectorComparisonProps = {
  comparison: IndustryComparison;
  carbon: number;
  employees?: number;
  turnover?: number;
  hideTurnover?: boolean;
};

function SectorComparison({
  comparison,
  carbon,
  employees,
  turnover,
  hideTurnover,
}: SectorComparisonProps) {
  const { t } = useTranslation(["common", "review"]);
  const location = useLocation();

  const isPublicPage = location.pathname.includes("/public");
  const shouldHideEconomicIntensity =
    (hideTurnover && isPublicPage) || !turnover;

  return (
    <Card className="flex h-full flex-col">
      <Typography step="2" as="h2" className="mb-sm">
        {t("review:comparison.bySector.title")}
      </Typography>
      <Tabs
        defaultValue={
          shouldHideEconomicIntensity
            ? COMPARISON_VIEWS.bySocialIntensity
            : COMPARISON_VIEWS.byEconomicIntensity
        }
        tabs={[
          {
            disabled: shouldHideEconomicIntensity,
            value: COMPARISON_VIEWS.byEconomicIntensity,
            label: t("review:comparison.bySector.economic"),
            PrefixIcon: Euro,
            content:
              turnover && turnover > 1 ? (
                <IntensityContent
                  // Convert from tCO2e/€ to tCO2e/100k€
                  percentiles={
                    (
                      Object.keys(
                        comparison.carbon_by_turnover
                      ) as Array<Percentile>
                    ).reduce(
                      (convertedPercentiles, key) => ({
                        ...convertedPercentiles,
                        [key]: comparison.carbon_by_turnover[key] * 100000.0,
                      }),
                      {}
                    ) as Percentiles
                  }
                  intensity={calculateEconomicCarbonIntensity(carbon, turnover)}
                  yourIntensity={t(
                    "review:comparison.bySector.yourEconomicIntensity"
                  )}
                  unit={t("common:unit.carbon_intensity.economic")}
                  pedagogicContent={t(
                    "review:comparison.pedagogicContent.economic"
                  )}
                  intensityMax={10}
                />
              ) : (
                <CannotCalculateIntensityMessage
                  message={t(
                    "review:comparison.cannotCalculateIntensityMessage.economic"
                  )}
                />
              ),
          },
          {
            value: COMPARISON_VIEWS.bySocialIntensity,
            label: t("review:comparison.bySector.social"),
            PrefixIcon: Users,
            content:
              employees && employees > 0 ? (
                <IntensityContent
                  percentiles={comparison.carbon_by_employee}
                  intensity={calculateSocialCarbonIntensity(carbon, employees)}
                  yourIntensity={t(
                    "review:comparison.bySector.yourSocialIntensity"
                  )}
                  unit={t("common:unit.carbon_intensity.social")}
                  pedagogicContent={t(
                    "review:comparison.pedagogicContent.social"
                  )}
                  intensityMax={20}
                />
              ) : (
                <CannotCalculateIntensityMessage
                  message={t(
                    "review:comparison.cannotCalculateIntensityMessage.social"
                  )}
                />
              ),
          },
        ]}
      />
    </Card>
  );
}

export default SectorComparison;
